/**
 * Section 1 — the post body.
 *
 * A plain textarea, not a rich-text editor. The formatting buttons wrap the
 * selection in the markers each network actually renders (Unicode bold/italic
 * would corrupt the copy, and HTML is not accepted by any of them), so what the
 * user types is exactly what is published.
 *
 * The counter strip is per-platform because one body goes to all of them at
 * once: a caption that fits LinkedIn can be 2,700 characters over X's limit, and
 * finding that out at publish time is too late.
 */

import { useRef } from 'react';
import {
  AtSign,
  Bold,
  Hash,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  MapPin,
  Smile,
  Sparkles,
  Underline,
  Video,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { PLATFORM_CONFIGS } from '../../../config/platforms';
import type { PlatformId } from '../../../config/platforms';
import { PlatformIcon } from './PlatformIcon';

/** Platforms whose caption limit is so high a counter is noise, not signal. */
const UNCOUNTED_PLATFORMS: PlatformId[] = ['facebook', 'youtube'];

interface PostEditorProps {
  text: string;
  onChange: (text: string) => void;
  selectedPlatforms: PlatformId[];
  onAiAssist: () => void;
  /** Opens the file picker owned by the Media section. */
  onAttachMedia: () => void;
}

export function PostEditor({
  text,
  onChange,
  selectedPlatforms,
  onAiAssist,
  onAttachMedia,
}: Readonly<PostEditorProps>) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const counted = selectedPlatforms.filter((p) => !UNCOUNTED_PLATFORMS.includes(p));
  const overLimit = counted.filter((p) => text.length > PLATFORM_CONFIGS[p].charLimits.caption);

  /**
   * Splices text around the caret and puts the caret back where the user would
   * expect it. Without the restore step every toolbar click would jump the caret
   * to the end of the post, which makes the buttons unusable mid-sentence.
   */
  const replaceSelection = (before: string, after: string) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const selected = text.slice(start, end);

    onChange(text.slice(0, start) + before + selected + after + text.slice(end));

    requestAnimationFrame(() => {
      if (!el) return;
      const caret = start + before.length + selected.length;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const onInsert = (value: string) => replaceSelection(value, '');
  const onWrap = (before: string, after: string) => replaceSelection(before, after);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      {/* ── Formatting toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50/70 px-2 py-1.5">
        <ToolButton label={t('composer.tool.bold')} onClick={() => onWrap('**', '**')}>
          <Bold className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.tool.italic')} onClick={() => onWrap('_', '_')}>
          <Italic className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.tool.underline')} onClick={() => onWrap('__', '__')}>
          <Underline className="h-[15px] w-[15px]" />
        </ToolButton>

        <Divider />

        <ToolButton label={t('composer.tool.bulletList')} onClick={() => onInsert('\n• ')}>
          <List className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.tool.numberedList')} onClick={() => onInsert('\n1. ')}>
          <ListOrdered className="h-[15px] w-[15px]" />
        </ToolButton>

        <Divider />

        <ToolButton label={t('composer.emoji')} onClick={() => onInsert('🎉')}>
          <Smile className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.hashtag')} onClick={() => onInsert('#')}>
          <Hash className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.tool.mention')} onClick={() => onInsert('@')}>
          <AtSign className="h-[15px] w-[15px]" />
        </ToolButton>
        <ToolButton label={t('composer.tool.link')} onClick={() => onInsert('https://')}>
          <LinkIcon className="h-[15px] w-[15px]" />
        </ToolButton>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onAiAssist}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-semibold text-blue-600 transition-colors hover:bg-blue-50"
        >
          <Sparkles className="h-[14px] w-[14px]" />
          {t('composer.aiAssistant')}
        </button>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={7}
        placeholder={t('composer.textPlaceholder')}
        className="block w-full resize-y border-0 px-4 py-3 text-[13.5px] leading-relaxed text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0"
      />

      {/* ── Attachment shortcuts + per-platform counters ───────────────────── */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-200 bg-slate-50/70 px-3 py-2">
        <div className="flex items-center gap-0.5">
          <ToolButton label={t('composer.tool.addImage')} onClick={onAttachMedia}>
            <ImageIcon className="h-[15px] w-[15px]" />
          </ToolButton>
          <ToolButton label={t('composer.tool.addGif')} onClick={onAttachMedia}>
            <span className="text-[10px] font-bold leading-none">GIF</span>
          </ToolButton>
          <ToolButton label={t('composer.tool.addVideo')} onClick={onAttachMedia}>
            <Video className="h-[15px] w-[15px]" />
          </ToolButton>
          <ToolButton label={t('composer.emoji')} onClick={() => onInsert('😊')}>
            <Smile className="h-[15px] w-[15px]" />
          </ToolButton>
          <ToolButton label={t('composer.tool.addLocation')} onClick={() => onInsert('📍 ')}>
            <MapPin className="h-[15px] w-[15px]" />
          </ToolButton>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2.5">
          {selectedPlatforms.map((platform) => (
            <CharCounter key={platform} platform={platform} length={text.length} />
          ))}
        </div>

        {selectedPlatforms.length > 0 && (
          <span
            className={cn(
              'flex items-center gap-1 text-[11px] font-semibold',
              overLimit.length > 0 ? 'text-rose-600' : 'text-emerald-600'
            )}
          >
            {overLimit.length > 0 ? (
              <>
                <AlertCircle className="h-3.5 w-3.5" />
                {t('composer.limitsExceeded', { count: overLimit.length })}
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('composer.limitsOk')}
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Characters *remaining* rather than characters used. Used is the same number on
 * every platform and so tells the user nothing; remaining is what decides
 * whether they have to cut the copy, and it is what goes negative.
 */
function CharCounter({ platform, length }: Readonly<{ platform: PlatformId; length: number }>) {
  const cfg = PLATFORM_CONFIGS[platform];
  const uncounted = UNCOUNTED_PLATFORMS.includes(platform);
  const remaining = cfg.charLimits.caption - length;
  const over = remaining < 0;
  const close = !over && remaining <= cfg.charLimits.caption * 0.1;

  return (
    <span
      className="flex items-center gap-1"
      title={
        uncounted
          ? cfg.name
          : `${cfg.name} — ${remaining.toLocaleString()} / ${cfg.charLimits.caption.toLocaleString()}`
      }
    >
      <PlatformIcon platform={platform} size={13} brandColor />
      <span
        className={cn(
          'text-[11px] font-semibold tabular-nums',
          over && 'text-rose-600',
          close && 'text-amber-600',
          !over && !close && 'text-slate-500'
        )}
      >
        {uncounted ? '—' : remaining.toLocaleString()}
      </span>
    </span>
  );
}

function ToolButton({
  label,
  onClick,
  children,
}: Readonly<{ label: string; onClick: () => void; children: React.ReactNode }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-slate-200" />;
}
